// tslint:disable:max-line-length
import { TCI_MARKETPLACE_LISTING_VIEW_MODE, TCI_MARKETPLACE_LISTING_TYPE, TCITenantAppType, TCITenantAppCategory } from "@tci/common";
import { Decision, BoolTable, Truth } from "booltable";
import { DecisionMonad, BoolTableMonad } from "booltable/dist/types";
import {
    IMkpV2ListContext,
    IMkpV2ListEvalRes,
    CONDITION_IS_ALLOWED_IN,
    CONDITION_STATUS_MANAGING,
    CONDITION_STATUS_CONSUMING,
    MKP_POPUP,
    MKP_ACTION_ITEMS,
    formatResult,
    isRO,
    isAdmin,
    ConditionsForStatusManaging,
    ConditionsForStatusConsuming
} from "./listing-bt-evaluations";
import { isEmpty, cloneDeep } from "lodash";

//#region - Conditions catalog binding condition string with the actual evaluation
const ConditionsForAllowed = (context: IMkpV2ListContext): BoolTableMonad =>
    BoolTable.of([
        [
            CONDITION_IS_ALLOWED_IN.LIST_CARD,
            Truth.of([
                [].map(item => item.toString()).includes(context.item.toString())
            ]).and()
        ],
        [
            CONDITION_IS_ALLOWED_IN.DETAIL,
            Truth.of([
                [
                    MKP_ACTION_ITEMS.PRICING_INFO,
                    MKP_ACTION_ITEMS.REQUEST_BUTTON,
                    MKP_ACTION_ITEMS.GET_BUTTON,
                    MKP_ACTION_ITEMS.UNINSTALL_LINK,
                    MKP_ACTION_ITEMS.INSTALLED_BUTTON,
                    MKP_ACTION_ITEMS.PENDING_REQUEST,
                    MKP_ACTION_ITEMS.REQUEST_REJECTED_STATUS,
                    MKP_ACTION_ITEMS.ACCESS_REVOKED_STATUS
                ].map(item => item.toString()).includes(context.item.toString())
            ]).and()
        ],
        [
            CONDITION_IS_ALLOWED_IN.PREVIEW_CARD,
            Truth.of([
                [].map(item => item.toString()).includes(context.item.toString())
            ]).and()
        ],
        [
            CONDITION_IS_ALLOWED_IN.PREVIEW_DETAIL,
            Truth.of([
                [
                    MKP_ACTION_ITEMS.PRICING_INFO,
                    MKP_ACTION_ITEMS.REQUEST_BUTTON,
                ].map(item => item.toString()).includes(context.item.toString())
            ]).and()
        ]
    ]);

// Use this function to see if an item is allowed in a certain mode
const CommonAllowanceCheck = (context: IMkpV2ListContext): IMkpV2ListEvalRes => {
    let condTable: DecisionMonad;
    switch (context.mode) {
        case TCI_MARKETPLACE_LISTING_VIEW_MODE.LIST_CARD:
            condTable = Decision.of([
                [ConditionsForAllowed(context).q(CONDITION_IS_ALLOWED_IN.LIST_CARD), { visible: true }]
            ]);
            break;
        case TCI_MARKETPLACE_LISTING_VIEW_MODE.LIST_DETAIL:
            condTable = Decision.of([
                [ConditionsForAllowed(context).q(CONDITION_IS_ALLOWED_IN.DETAIL), { visible: true }]
            ]);
            break;
        case TCI_MARKETPLACE_LISTING_VIEW_MODE.PREVIEW_CARD:
            condTable = Decision.of([
                [ConditionsForAllowed(context).q(CONDITION_IS_ALLOWED_IN.PREVIEW_CARD), { visible: true }]
            ]);
            break;
        case TCI_MARKETPLACE_LISTING_VIEW_MODE.PREVIEW_DETAIL:
            condTable = Decision.of([
                [ConditionsForAllowed(context).q(CONDITION_IS_ALLOWED_IN.PREVIEW_DETAIL), { visible: true }]
            ]);
            break;
    }

    return condTable.run("any").chain(formatResult);
};
//#endregion

const CheckPricing = (context: IMkpV2ListContext): IMkpV2ListEvalRes => {
    const allowanceResult = CommonAllowanceCheck(context);

    // Break if pricing info is not present
    if (
        isEmpty(context.asset.pricingInfo) ||
        (context.asset.pricingInfo.details.length === 0 && context.asset.pricingInfo.pricingOptions.length === 0)
    ) {
        allowanceResult.visible = false;
        return allowanceResult;
    }

    // If visible by mode then check by status managing
    if (allowanceResult.visible) {
        // For Connector the pricing won't be displayed when it's installed
        const isConnector = context.asset.listingType === TCI_MARKETPLACE_LISTING_TYPE.Connector;

        const condTable = Decision.of([
            [ConditionsForStatusManaging(context).q(CONDITION_STATUS_MANAGING.IS_PRIVATELY_AVAILABLE), { visible: true }],
            [ConditionsForStatusManaging(context).q(CONDITION_STATUS_MANAGING.IS_PENDING_APPROVAL), { visible: true }],
            [ConditionsForStatusManaging(context).q(CONDITION_STATUS_MANAGING.IS_PUBLICLY_AVAILABLE), { visible: true }],

            [ConditionsForStatusConsuming(context).q(CONDITION_STATUS_CONSUMING.IS_LISTING_REQUEST), { visible: true, enabled: true }],
            [ConditionsForStatusConsuming(context).q(CONDITION_STATUS_CONSUMING.IS_REQUEST_APPROVED), { visible: true, enabled: true }],
            [ConditionsForStatusConsuming(context).q(CONDITION_STATUS_CONSUMING.IS_LISTING_ENABLED), { visible: !isConnector, enabled: !isConnector }],
            [ConditionsForStatusConsuming(context).q(CONDITION_STATUS_CONSUMING.IS_LISTING_GET), { visible: true, enabled: true }],
            [ConditionsForStatusConsuming(context).q(CONDITION_STATUS_CONSUMING.IS_REVOKE_AKNOWLEDGED), { visible: true, enabled: true }],

            [ConditionsForStatusConsuming(context).q(CONDITION_STATUS_CONSUMING.IS_ACCESS_REVOKED), { visible: false, enabled: false }],
            [ConditionsForStatusConsuming(context).q(CONDITION_STATUS_CONSUMING.IS_REQUEST_REJECTED), { visible: false, enabled: false }]
        ]);
        return condTable.run("any").chain(formatResult);
    }
    return allowanceResult;
};

export const CheckRequestButton = (context: IMkpV2ListContext): IMkpV2ListEvalRes => {
    const allowanceResult = CommonAllowanceCheck(context);

    // IPAS-17203
    allowanceResult.visible = allowanceResult.visible &&
        context.asset.listingType !== TCI_MARKETPLACE_LISTING_TYPE.Accelerator;

    if (context.mode !== TCI_MARKETPLACE_LISTING_VIEW_MODE.PREVIEW_DETAIL && allowanceResult.visible) {
        const condTable = Decision.of([
            // IPAS-17098 - RO should see it disabled
            [ConditionsForStatusConsuming(context).q(CONDITION_STATUS_CONSUMING.IS_LISTING_REQUEST), { visible: true, enabled: !isRO(context) }],
            [ConditionsForStatusConsuming(context).q(CONDITION_STATUS_CONSUMING.IS_PENDING_REQUEST), { visible: true, enabled: !isRO(context) }]
        ]);

        return condTable.run("any").chain(formatResult);
    }

    return allowanceResult;
};

export const CheckRequesPending = (context: IMkpV2ListContext): IMkpV2ListEvalRes => {
    const allowanceResult = CommonAllowanceCheck(context);

    if (context.mode !== TCI_MARKETPLACE_LISTING_VIEW_MODE.PREVIEW_DETAIL && allowanceResult.visible) {
        const condTable = Decision.of([
            [ConditionsForStatusConsuming(context).q(CONDITION_STATUS_CONSUMING.IS_PENDING_REQUEST), { visible: true, enabled: true }]
        ]);

        return condTable.run("any").chain(formatResult);
    }

    return allowanceResult;
};

export const CheckGetButton = (context: IMkpV2ListContext): IMkpV2ListEvalRes => {
    const allowanceResult = CommonAllowanceCheck(context);

    // IPAS-17203
    allowanceResult.visible = allowanceResult.visible &&
        context.asset.listingType !== TCI_MARKETPLACE_LISTING_TYPE.Accelerator;

    if (allowanceResult.visible) {
        const condTable = Decision.of([
            // IPAS-17098 - RO should see it disabled
            [ConditionsForStatusConsuming(context).q(CONDITION_STATUS_CONSUMING.IS_REQUEST_APPROVED), { visible: true, enabled: !isRO(context) }],
            [ConditionsForStatusConsuming(context).q(CONDITION_STATUS_CONSUMING.IS_LISTING_GET), { visible: true, enabled: !isRO(context) }]
        ]);
        return condTable.run("any").chain(formatResult);
    }
    return allowanceResult;
};

export const CheckUninstallLink = (context: IMkpV2ListContext): IMkpV2ListEvalRes => {
    const allowanceResult = CommonAllowanceCheck(context);

    // IPAS-17056
    const isOOTBFlogoBWConnector = context.asset.listingType === TCI_MARKETPLACE_LISTING_TYPE.Connector &&
        context.asset.enabledByDefault &&
        [TCITenantAppCategory.DEVELOP, TCITenantAppCategory.INTEGRATE].some(category => category.id === context.asset.tenantCategory.id);

    const isScribeConnector = context.asset.listingType === TCI_MARKETPLACE_LISTING_TYPE.Connector &&
        context.asset.tenantType.equals(TCITenantAppType.scribe);

    allowanceResult.visible = allowanceResult.visible && isAdmin(context) && !isOOTBFlogoBWConnector && isScribeConnector;

    if (allowanceResult.visible) {
        const condTable = Decision.of([
            [ConditionsForStatusConsuming(context).q(CONDITION_STATUS_CONSUMING.IS_LISTING_ENABLED), {
                visible: true,
                enabled: !isRO(context)
            }]
        ]);
        return condTable.run("any").chain(formatResult);
    }
    return allowanceResult;
};

export const CheckInstalledStatus = (context: IMkpV2ListContext): IMkpV2ListEvalRes => {
    const allowanceResult = CommonAllowanceCheck(context);

    allowanceResult.visible = allowanceResult.visible &&
        context.asset.listingType !== TCI_MARKETPLACE_LISTING_TYPE.Accelerator; // IPAS-17143 - Not for Accelerator

    // If visible by mode then check by status managing
    if (allowanceResult.visible) {
        const condTable = Decision.of([
            [ConditionsForStatusConsuming(context).q(CONDITION_STATUS_CONSUMING.IS_LISTING_ENABLED), { visible: true, enabled: !isRO(context) }]
        ]);
        return condTable.run("any").chain(formatResult);
    }
    return allowanceResult;
};

export const CheckRequestRejectedStatus = (context: IMkpV2ListContext): IMkpV2ListEvalRes => {
    const allowanceResult = CommonAllowanceCheck(context);

    // If visible by mode then check by status managing
    if (allowanceResult.visible) {
        const condTable = Decision.of([
            [ConditionsForStatusConsuming(context).q(CONDITION_STATUS_CONSUMING.IS_REQUEST_REJECTED), { visible: true, enabled: !isRO(context) }]
        ]);

        return condTable.run("any").chain(formatResult);
    }

    return allowanceResult;
};

export const CheckAccessRevokedStatus = (context: IMkpV2ListContext): IMkpV2ListEvalRes => {
    const allowanceResult = CommonAllowanceCheck(context);

    // If visible by mode then check by status managing
    if (allowanceResult.visible) {
        const condTable = Decision.of([
            [ConditionsForStatusConsuming(context).q(CONDITION_STATUS_CONSUMING.IS_ACCESS_REVOKED), { visible: true, enabled: !isRO(context) }]
        ]);

        return condTable.run("any").chain(formatResult);
    }

    return allowanceResult;
};

// Evaluations for Popup
// We need to evaluate GetButton first to even know if the Popup is going to be displayed
const EvaluateGetButton = (context: IMkpV2ListContext): IMkpV2ListEvalRes => {
    const GetBtnContext = Object.assign(cloneDeep(context), { item: MKP_ACTION_ITEMS.GET_BUTTON });
    return CheckOverActionItem(GetBtnContext);
};

export const CheckScribeConnector = (context: IMkpV2ListContext): IMkpV2ListEvalRes => { // Can Install
    const result = EvaluateGetButton(context);
    const isScribe = context.asset.tenantType.equals(TCITenantAppType.scribe) &&
        context.asset.listingType === TCI_MARKETPLACE_LISTING_TYPE.Connector;
    result.visible = result.visible && isScribe;
    result.enabled = result.enabled && isScribe && !isRO(context);
    return result;
};

export const CheckConnector = (context: IMkpV2ListContext): IMkpV2ListEvalRes => { // Not Uninstallable
    const result = EvaluateGetButton(context);
    const isFlogo = context.asset.tenantType.equals(TCITenantAppType.flogo);
    const isConnector = context.asset.listingType === TCI_MARKETPLACE_LISTING_TYPE.Connector;
    result.visible = result.visible && isFlogo && isConnector;
    result.enabled = result.enabled && isFlogo && isConnector && !isRO(context);
    return result;
};

export const CheckApp = (context: IMkpV2ListContext): IMkpV2ListEvalRes => {
    const result = EvaluateGetButton(context);
    const isApp = context.asset.listingType === TCI_MARKETPLACE_LISTING_TYPE.Application;
    result.visible = result.visible && isApp;
    result.enabled = result.enabled && isApp && !isRO(context);
    let isDownloadable = false;
    if (context.asset.assetRef && context.asset.assetRef.length > 0) {
        context.asset.assetRef.forEach((asset) => {
          if (asset.isDownloadAvailable) {
            isDownloadable = true;
          }
        });
    }
    result.isDownloadable = isDownloadable;
    return result;
};

export const CheckOverActionItem = (context: IMkpV2ListContext): IMkpV2ListEvalRes => {
    switch (context.item) {
        case MKP_ACTION_ITEMS.PRICING_INFO:
            return CheckPricing(context);

        case MKP_ACTION_ITEMS.REQUEST_BUTTON:
            return CheckRequestButton(context);

        case MKP_ACTION_ITEMS.PENDING_REQUEST:
            return CheckRequesPending(context);

        case MKP_ACTION_ITEMS.GET_BUTTON:
            return CheckGetButton(context);

        case MKP_ACTION_ITEMS.UNINSTALL_LINK:
            return CheckUninstallLink(context);

        case MKP_ACTION_ITEMS.INSTALLED_BUTTON:
            return CheckInstalledStatus(context);

        case MKP_ACTION_ITEMS.REQUEST_REJECTED_STATUS:
            return CheckRequestRejectedStatus(context);

        case MKP_ACTION_ITEMS.ACCESS_REVOKED_STATUS:
            return CheckAccessRevokedStatus(context);

        // For Popup
        case MKP_POPUP.GET_ENABLE_SCRIBE:
            return CheckScribeConnector(context);
        case MKP_POPUP.GET_ENABLE_CONNECTOR:
            return CheckConnector(context);
        case MKP_POPUP.GET_ENABLE_APP:
            return CheckApp(context);
    }
};
